import { useMemo } from "react";
import { useIntl } from "react-intl";

import {
  editableCellClass,
  editableCellCorrectClass,
  editableCellIncorrectClass,
} from "../TableQuestion.css";

export interface UseEditableCellStateParams {
  value: string | number | null | undefined;
  showFeedback?: boolean;
  isCorrect?: boolean;
  feedback?: string;
  hint?: string;
  isPrediction?: boolean;
}

export interface EditableCellState {
  /** Whether the cell has a non-empty value */
  hasValue: boolean;
  /** CSS class for the cell container based on feedback state */
  cellClass: string;
  /** Input/Select state for styling: "default" | "success" | "error" */
  state: "default" | "success" | "error";
  /** Suffix icon to display: "check" | "cross" | undefined */
  suffixIcon: "check" | "cross" | undefined;
  /** Translated feedback message or undefined */
  translatedFeedback: string | undefined;
  /** Translated hint message or undefined */
  translatedHint: string | undefined;
  /** Whether feedback should be shown (not prediction mode, has feedback enabled, has value) */
  shouldShowFeedback: boolean;
}

/**
 * Hook to extract common feedback/hint state logic for editable table cells.
 * Used by InputCell and DropdownCell to avoid code duplication.
 */
export const useEditableCell = ({
  value,
  showFeedback,
  isCorrect,
  feedback,
  hint,
  isPrediction,
}: UseEditableCellStateParams): EditableCellState => {
  const intl = useIntl();

  return useMemo(() => {
    const hasValue = value !== null && value !== undefined && value !== "";

    const getCellClass = (): string => {
      if (isPrediction) {
        return editableCellClass;
      }
      if (showFeedback && hasValue) {
        return isCorrect
          ? editableCellCorrectClass
          : editableCellIncorrectClass;
      }
      return editableCellClass;
    };

    const getState = (): "default" | "success" | "error" => {
      if (isPrediction) return "default";
      if (!showFeedback || !hasValue) return "default";
      return isCorrect ? "success" : "error";
    };

    const getSuffixIcon = (): "check" | "cross" | undefined => {
      if (isPrediction) return undefined;
      if (!showFeedback || !hasValue) return undefined;
      return isCorrect ? "check" : "cross";
    };

    const translatedFeedback =
      !isPrediction && showFeedback && feedback && hasValue
        ? intl.formatMessage({ id: feedback, defaultMessage: feedback })
        : undefined;

    const translatedHint = hint
      ? intl.formatMessage({ id: hint, defaultMessage: hint })
      : undefined;

    const shouldShowFeedback = !isPrediction && !!showFeedback && hasValue;

    return {
      hasValue,
      cellClass: getCellClass(),
      state: getState(),
      suffixIcon: getSuffixIcon(),
      translatedFeedback,
      translatedHint,
      shouldShowFeedback,
    };
  }, [value, showFeedback, isCorrect, feedback, hint, isPrediction, intl]);
};
